import React from "react";
import ProfileCard from "../components/ProfileCard";
import profilesData from "../data/profiles.json";

interface Profile {
  id: string;
  type: string;
  title: string;
  shortDescription: string;
  longDescription: string;
  image: string;
  youtubeVideoId: string;
  gumroadLink: string;
  heroImages: string;
}

// Create a default or "empty" profile
const defaultProfile: Profile = {
  id: "",
  type: "",
  title: "Profile Not Found",
  shortDescription: "",
  longDescription: "",
  image: "",
  youtubeVideoId: "",
  gumroadLink: "",
  heroImages: "",
};

function getProfileById(id: string): Profile {
  const profiles: Profile[] = profilesData as Profile[];
  const foundProfile = profiles.find((profile) => profile.id === id);
  return foundProfile || defaultProfile; // Return defaultProfile if not found
}

const Home: React.FC = () => {
  return (
    <div className="container mx-auto p-8 text-white max-w-[800px]">
      <section className="pt-8 text-center">
        <h1 className="text-4xl sm:text-6xl font-bold mb-4 gradient-text">
          Welcome to PWC Profiles
        </h1>
        <h2 className="text-xl mb-8 opacity-70">
          High-quality Kemper Profiler profiles and TONEX tone models,
          meticulously crafted for authentic tone.
        </h2>
      </section>
      <section className="container mx-auto p-4 text-center py-16">
        <a
          href="https://paperworldcreation.gumroad.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg text-nowrap"
        >
          Visit Our Shop
        </a>
      </section>
      <section className="text-left font-light text-xl ">
        <h2 className="text-2xl font-bold uppercase">
          Why Choose PWC Kemper & ToneX Profiles?
        </h2>
        <ul className="list-disc p-4 space-y-2">
          <li>
            <span className="font-bold">Iconic Amp Sounds:</span> Capture the
            legendary sound of sought-after amps, meticulously profiled.
          </li>
          <li>
            <span className="font-bold">Real Amp Feel:</span> Experience the
            nuances and dynamic response of real tube amplifiers, without the
            weight or cost.
          </li>
          <li>
            <span className="font-bold">Expertly Crafted:</span> Dialed in by
            experienced sound engineers for instant, stage- or studio-ready
            sound.
          </li>
          <li>
            <span className="font-bold">Signature Artist Tones:</span> Get the
            iconic guitar tones of John Petrucci (Dream Theater) and other
            legendary artists.
          </li>
        </ul>
      </section>{" "}
      <section className="mt-12">
        <h2 className="text-2xl font-bold uppercase gradient-text">
          Free sample profile and tone model pack - DEMO
        </h2>
        <div className="flex justify-center mt-8 lg:w-1/2 lg:mx-auto">
          {" "}
          <ProfileCard
            highlight={true}
            profile={getProfileById("free-sample-pack")}
          />
        </div>
      </section>
      <section className="mt-24">
        <h1 className="text-2xl font-bold uppercase gradient-text">
          Latest Releases
        </h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8  mt-8">
          <ProfileCard profile={getProfileById("iaw-tonex")} />
          <ProfileCard profile={getProfileById("mark2a-nam")} />
        </div>
      </section>
      <section className="my-16 text-center items-center flex justify-center">
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/w7u-iYZG3WA?si=XQFH5Xd3-_Oii-lM"
          title="Mesa Boogie Mark IIA Demo Video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        ></iframe>
      </section>
      <section className="my-16 text-center ">
        <div className="flex justify-center items-center mb-8 ">
          <a
            href="/kemper"
            className="hover:scale-125 transition-transform"
            aria-label="Kemper Profiles"
          >
            <img
              src="/images/kemper_logo.png" // Absolute path from root
              alt="Kemper Profiler Logo"
              className="h-32 "
            />
          </a>
        </div>
      </section>
      <section className="mt-12">
        <h1 className="text-2xl font-bold uppercase gradient-text">
          Our best selling Kemper profile packs
        </h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8  mt-8">
          <ProfileCard
            profile={getProfileById("dt-everything-kemper")}
          ></ProfileCard>
          <ProfileCard
            profile={getProfileById("mesa-boogie-mk-35-kemper")}
          ></ProfileCard>
        </div>
      </section>{" "}
      <section className="my-16 text-center ">
        <div className="flex justify-center items-center mb-8 gap-16">
          <a
            href="/tonex"
            className="hover:scale-125 transition-transform"
            aria-label="Tonex tone models"
          >
            <img
              src="/images/ToneX_Logo.svg" // Absolute path from root
              alt="ToneX Pedal Logo"
              className="h-32"
            />
          </a>
        </div>
      </section>
      <section className="mt-12">
        <h1 className="text-2xl font-bold uppercase gradient-text">
          Our best selling Tonex tone model packs
        </h1>
        <div className="grid grid-cols-1 sm:grid-cols-2  justify-evenly gap-8 mt-8">
          <ProfileCard profile={getProfileById("sfam-tonex")}></ProfileCard>
          <ProfileCard profile={getProfileById("srv-tonex")}></ProfileCard>
        </div>
      </section>
      <section className="my-16 text-center ">
        <div className="flex justify-center items-center mb-8 gap-16">
          <a
            href="/kemper"
            className="hover:scale-125 transition-transform"
            aria-label="Kemper Profiles"
          >
            <img
              src="/images/kemper_logo.png" // Absolute path from root
              alt="Kemper Profiler Logo"
              className="h-32 mr-4"
            />
          </a>
          <a
            href="/tonex"
            className="hover:scale-125 transition-transform"
            aria-label="Tonex tone models"
          >
            <img
              src="/images/ToneX_Logo.svg" // Absolute path from root
              alt="ToneX Pedal Logo"
              className="h-32"
            />
          </a>
        </div>
        <h2 className="text-xl font-bold gradient-text">
          Many more sounds are available for the Kemper Profiler and TONEX
          Pedal. Choose your platform.
        </h2>
      </section>
      <section className="mt-8 ">
        <p className="text-sm opacity-50">
          PWC Profiles is not affiliated, associated, or in any way connected
          with the owners of the amplifier brands mentioned. All trademarks and
          copyrights belong to their respective owners. The use of these names
          is purely for descriptive and comparative purposes in the context of
          Kemper Profiles and Tonex models, which aim to replicate the sonic
          qualities of the original amplifiers.
        </p>
      </section>
    </div>
  );
};

export default Home;
